import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { listForms } from "api/endpoints/forms";

const SectionLabel = ({ children }) => (
  <MDTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
    {children}
  </MDTypography>
);
SectionLabel.propTypes = { children: PropTypes.node.isRequired };

// IANA timezone names — supported by modern browsers; fall back to empty list.
const TIMEZONES =
  typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];

/**
 * <EventFields> — the Event type block. Events are first-class content:
 * online / offline / hybrid, organized by the community, the organization or a
 * third party. Venue, online-URL and registration fields are conditionally
 * required; the backend re-validates the resolved state.
 */
function EventFields({ value, onChange, websiteId }) {
  const [forms, setForms] = useState([]);

  useEffect(() => {
    if (!websiteId) return;
    listForms(websiteId)
      .then((res) => setForms(res.items))
      .catch(() => setForms([]));
  }, [websiteId]);

  const showVenue = ["offline", "hybrid"].includes(value.event_mode);
  const showOnlineUrl = ["online", "hybrid"].includes(value.event_mode);
  const showRegistrationUrl = value.registration_type === "external_link";
  const showRegistrationForm = value.registration_type === "form";

  const timezoneValue = useMemo(() => value.timezone || null, [value.timezone]);

  return (
    <Stack spacing={3}>
      <SectionLabel>Event details</SectionLabel>

      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(value.is_featured)}
            onChange={(e) => onChange({ is_featured: e.target.checked })}
          />
        }
        label="Featured event"
      />

      <TextField
        select
        label="Event mode"
        value={value.event_mode}
        onChange={(e) => onChange({ event_mode: e.target.value })}
        fullWidth
        required
      >
        <MenuItem value="" disabled>
          Select a mode
        </MenuItem>
        <MenuItem value="online">Online</MenuItem>
        <MenuItem value="offline">Offline</MenuItem>
        <MenuItem value="hybrid">Hybrid</MenuItem>
      </TextField>

      <TextField
        label="Event start"
        type="datetime-local"
        value={value.event_start_at}
        onChange={(e) => onChange({ event_start_at: e.target.value })}
        fullWidth
        required
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="Event end"
        type="datetime-local"
        value={value.event_end_at}
        onChange={(e) => onChange({ event_end_at: e.target.value })}
        fullWidth
        required
        InputLabelProps={{ shrink: true }}
      />
      <Autocomplete
        options={TIMEZONES}
        value={timezoneValue}
        onChange={(_, next) => onChange({ timezone: next || "" })}
        renderInput={(params) => (
          <TextField {...params} label="Timezone" helperText="Optional — IANA name" />
        )}
      />

      <Divider />
      <SectionLabel>Organizer</SectionLabel>

      <TextField
        select
        label="Organizer type"
        value={value.organizer_type}
        onChange={(e) => onChange({ organizer_type: e.target.value })}
        fullWidth
        required
      >
        <MenuItem value="" disabled>
          Select an organizer type
        </MenuItem>
        <MenuItem value="community">Community</MenuItem>
        <MenuItem value="organization">Organization</MenuItem>
        <MenuItem value="third_party">Third party</MenuItem>
      </TextField>
      <TextField
        label="Organizer name"
        value={value.organizer_name}
        onChange={(e) => onChange({ organizer_name: e.target.value })}
        fullWidth
      />
      <TextField
        label="Organizer URL"
        type="url"
        value={value.organizer_url}
        onChange={(e) => onChange({ organizer_url: e.target.value })}
        fullWidth
      />

      {(showVenue || showOnlineUrl) && (
        <>
          <Divider />
          <SectionLabel>Location</SectionLabel>
        </>
      )}
      {showVenue && (
        <>
          <TextField
            label="Venue name"
            value={value.venue_name}
            onChange={(e) => onChange({ venue_name: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label="Venue address"
            value={value.venue_address}
            onChange={(e) => onChange({ venue_address: e.target.value })}
            fullWidth
            multiline
            minRows={2}
          />
        </>
      )}
      {showOnlineUrl && (
        <TextField
          label="Online URL"
          type="url"
          value={value.online_url}
          onChange={(e) => onChange({ online_url: e.target.value })}
          fullWidth
          required
        />
      )}

      <Divider />
      <SectionLabel>Registration</SectionLabel>

      <TextField
        select
        label="Registration type"
        value={value.registration_type}
        onChange={(e) => onChange({ registration_type: e.target.value })}
        fullWidth
      >
        <MenuItem value="none">None</MenuItem>
        <MenuItem value="external_link">External link</MenuItem>
        <MenuItem value="form">Form</MenuItem>
      </TextField>
      {showRegistrationUrl && (
        <TextField
          label="Registration URL"
          type="url"
          value={value.registration_url}
          onChange={(e) => onChange({ registration_url: e.target.value })}
          fullWidth
          required
        />
      )}
      {showRegistrationForm && (
        <TextField
          select
          label="Registration form"
          value={value.registration_form_id || ""}
          onChange={(e) => onChange({ registration_form_id: e.target.value })}
          fullWidth
          required
          helperText={forms.length === 0 ? "No forms defined for this website" : undefined}
        >
          <MenuItem value="" disabled>
            Select a form
          </MenuItem>
          {forms.map((form) => (
            <MenuItem key={form.form_id} value={form.form_id}>
              {form.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      <TextField
        label="Registration deadline"
        type="datetime-local"
        value={value.registration_deadline}
        onChange={(e) => onChange({ registration_deadline: e.target.value })}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(value.is_invite_only)}
            onChange={(e) => onChange({ is_invite_only: e.target.checked })}
          />
        }
        label="Invite only"
      />

      <Divider />
      <SectionLabel>Pricing &amp; capacity</SectionLabel>

      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(value.is_paid)}
            onChange={(e) => onChange({ is_paid: e.target.checked })}
          />
        }
        label="Paid event"
      />
      <MDBox display="flex" gap={2}>
        <TextField
          label="Fee amount"
          type="number"
          value={value.fee_amount}
          onChange={(e) => onChange({ fee_amount: e.target.value })}
          fullWidth
          helperText="Display only — no payment is processed"
        />
        <TextField
          label="Fee currency"
          value={value.fee_currency}
          onChange={(e) => onChange({ fee_currency: e.target.value })}
          fullWidth
          inputProps={{ maxLength: 3 }}
          helperText="3-letter code, e.g. INR"
        />
      </MDBox>
      <TextField
        label="Capacity"
        type="number"
        value={value.capacity}
        onChange={(e) => onChange({ capacity: e.target.value })}
        fullWidth
        helperText="Informational"
      />

      <Divider />
      <SectionLabel>Programme</SectionLabel>

      <TextField
        label="Speakers"
        value={value.speakers}
        onChange={(e) => onChange({ speakers: e.target.value })}
        fullWidth
        multiline
        minRows={3}
      />
      <TextField
        label="Agenda"
        value={value.agenda}
        onChange={(e) => onChange({ agenda: e.target.value })}
        fullWidth
        multiline
        minRows={3}
      />
      <TextField
        label="Recording URL"
        type="url"
        value={value.recording_url}
        onChange={(e) => onChange({ recording_url: e.target.value })}
        fullWidth
        helperText="Optional — added after the event"
      />
    </Stack>
  );
}

EventFields.defaultProps = {
  websiteId: undefined,
  context: {},
};

EventFields.propTypes = {
  value: PropTypes.shape({
    is_featured: PropTypes.bool,
    event_mode: PropTypes.string,
    organizer_type: PropTypes.string,
    organizer_name: PropTypes.string,
    organizer_url: PropTypes.string,
    event_start_at: PropTypes.string,
    event_end_at: PropTypes.string,
    timezone: PropTypes.string,
    venue_name: PropTypes.string,
    venue_address: PropTypes.string,
    online_url: PropTypes.string,
    registration_type: PropTypes.string,
    registration_url: PropTypes.string,
    registration_form_id: PropTypes.string,
    registration_deadline: PropTypes.string,
    is_invite_only: PropTypes.bool,
    is_paid: PropTypes.bool,
    fee_amount: PropTypes.string,
    fee_currency: PropTypes.string,
    capacity: PropTypes.string,
    recording_url: PropTypes.string,
    speakers: PropTypes.string,
    agenda: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  websiteId: PropTypes.string,
  context: PropTypes.object,
};

export default EventFields;
